import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { useMediaQuery } from 'react-responsive'
import { isEmpty } from 'lodash'

// Actions
import { fetchSavedSearches } from 'src/actions/savedSearches'
import { setActiveTab } from 'src/actions/navigation'

// Selectors
import { selectClientIsGuest } from 'src/store/selectors/client'
import { selectCustomerId } from 'src/store/selectors/customerProfile'
import {
  selectSavedSearches,
  selectSavedSearchesLoading,
} from 'src/store/selectors/savedSearches'

// Components
import { BotHeadSpinWithMessage, If, Text, Unless } from 'src/Components'
import SavedSearch from 'src/Components/homeSearch/SavedSearch'
import EmptyState from 'src/Components/homeSearch/EmptyState'

import { TAB_NAMES } from 'src/constants/navigation'

// Styles
import styles from './SavedSearches.module.scss'

const SavedSearches = () => {
  const customerId = useSelector(selectCustomerId)
  const dispatch = useDispatch()
  const savedSearchesLoading = useSelector(selectSavedSearchesLoading)
  const savedSearches = useSelector(selectSavedSearches)
  const clientIsGuest = useSelector(selectClientIsGuest)
  const isMediumScreen = useMediaQuery({
    query: `(min-width: ${styles.mediumBreak})`,
  })

  useEffect(() => {
    dispatch(setActiveTab(TAB_NAMES.SAVED))
  }, [dispatch])

  useEffect(() => {
    if (!clientIsGuest) {
      dispatch(fetchSavedSearches(customerId))
    }
  }, [dispatch, clientIsGuest, customerId])

  if (!clientIsGuest && savedSearchesLoading) {
    return (
      <BotHeadSpinWithMessage className={styles.loadingContainer}>
        <FormattedMessage
          id='BotHeadSpinWithMessage.SavedSearches'
          defaultMessage='Loading your saved searches...'
        />
      </BotHeadSpinWithMessage>
    )
  }

  return (
    customerId && (
      <div className={styles.savedSearchesWrapper}>
        <div className={styles.savedSearchesHeader}>
          <Text
            element='h1'
            size={isMediumScreen ? 'xl' : 's'}
            align={isMediumScreen ? 'left' : 'center'}
            weight={isMediumScreen ? 'bold' : 'semibold'}
            variant='neutralCool'
            variantCount='900'
          >
            <FormattedMessage
              id='HomeSearch.savedSearches.heading'
              defaultMessage='Saved searches'
            />
          </Text>
          {savedSearches?.length > 0 && (
            <span>
              <Text
                className={styles.headerBadge}
                size='xxs'
                weight='bold'
                variant='neutralCool'
                variantCount='100'
                element='span'
              >
                {savedSearches.length}
              </Text>
            </span>
          )}
        </div>
        <Unless condition={isEmpty(savedSearches)}>
          {savedSearches?.map(savedSearch => (
            <>
              <SavedSearch key={savedSearch.id} savedSearch={savedSearch} />
              <hr className={styles.savedSearchesDivider} />
            </>
          ))}
        </Unless>
        <If condition={isEmpty(savedSearches)}>
          <EmptyState
            copy={
              <FormattedMessage
                id='HomeSearch.savedSearches.emptyState.copy'
                defaultMessage='You have no saved searches. Search for a city or zip, add filters, and turn alerts on.'
              />
            }
            ctaCopy={
              <FormattedMessage
                id='HomeSearch.savedSearches.emptyState.cta'
                defaultMessage='Start search'
              />
            }
          />
        </If>
      </div>
    )
  )
}

export default SavedSearches
